import { Matrix4, Vector3 } from "three";

import ExtrudedLoopAnnotationData from "./ExtrudedLoopAnnotationData";
import SceneObjectTypeEnum from "./SceneObjectTypeEnum";
import Placement3d from "../Placement3d";
import { vector3FromJson } from "../../services/bimVisualizerApi/primitivesSerialization";
import { UUID } from "../../utils/typeDefinitions";
import { mmFromSceneUnits, sceneUnitsFromMm } from "../../utils/scaler";
import circleAnnotationDataSchema from "../../services/bimVisualizerApi/schema/document/sceneObject/circleAnnotationDataSchema";
import EditHandleTypeEnum from "../../enums/EditHandleTypeEnum";
import { DragMeasurement, formatCm } from "../../utils/dragMeasurement";

class CircleAnnotationData extends ExtrudedLoopAnnotationData {

    // Smoothness of the rendered/hit-tested outline. The exact area (computeEnclosedArea) does not
    // depend on this — it's only used for getLoopPoints3d()'s N-gon approximation.
    static readonly SEGMENT_COUNT = 48;

    radius: number;

    constructor(    placement: Placement3d,
                    radius: number,
                    depth: number = 0,
                    extrudedDirection: Vector3 = new Vector3(0, 0, 1),
                    fontSize: number = CircleAnnotationData.DEFAULT_FONT_SIZE,
                    showAreaText: boolean = true,
                    showVolume: boolean = false,
                    uuid?: UUID,
                    parentId?: UUID) {
        super(SceneObjectTypeEnum.CircleAnnotation, placement, depth, extrudedDirection, fontSize, showAreaText, showVolume, uuid, parentId);
        this.radius = radius;
    }

    clone(): this {
        const clone = new CircleAnnotationData(
                                        this.placement.clone() as Placement3d,
                                        this.radius,
                                        this.depth,
                                        this.extrudedDirection.clone(),
                                        this.fontSize,
                                        this.showAreaText,
                                        this.showVolume,
                                        this.uuid, this.parentId) as this;

        this.copyBaseData(clone);
        return clone;
    }

    protected getLoopPoints3d(): Vector3[] {
        const localX = this.placement.refDirection.clone().normalize();
        const localY = this.placement.axis.clone().cross(localX).normalize();

        const points: Vector3[] = [];
        for (let i = 0; i < CircleAnnotationData.SEGMENT_COUNT; i++) {
            const theta = (i / CircleAnnotationData.SEGMENT_COUNT) * Math.PI * 2;
            points.push(
                localX.clone().multiplyScalar(this.radius * Math.cos(theta))
                    .add(localY.clone().multiplyScalar(this.radius * Math.sin(theta)))
            );
        }
        return points;
    }

    // Exact area — not the N-gon shoelace approximation getProfilePoints2d() would give.
    computeEnclosedArea(): number {
        return Math.PI * this.radius * this.radius;
    }

    getDragMeasurement(handleType: EditHandleTypeEnum): DragMeasurement | undefined {
        if (handleType !== EditHandleTypeEnum.CIRCLE_RADIUS) return super.getDragMeasurement(handleType);

        return { labelKey: 'properties.field.circleRadius.name', text: formatCm(this.radius) };
    }

    applyMatrix4(matrix: Matrix4): void {
        this.applyMatrix4Base(matrix);
        // radius is a scalar magnitude; every applyMatrix4() call site in this codebase passes a
        // rotation-only (uniform-scale) transform, so it needs no adjustment here.
    }

    toJson(): any {
        return {
            ...this.toJsonBase(),
            radius: Math.round(mmFromSceneUnits(this.radius) * 1000) / 1000,
        };
    }

    static fromJson(json: any): CircleAnnotationData {

        const parsedData = circleAnnotationDataSchema.parse(json);

        return new CircleAnnotationData(
            new Placement3d(
                vector3FromJson(parsedData.placement.location, sceneUnitsFromMm),
                vector3FromJson(parsedData.placement.refDirection),
                vector3FromJson(parsedData.placement.axis)
            ),
            sceneUnitsFromMm(parsedData.radius),
            sceneUnitsFromMm(parsedData.depth),
            vector3FromJson(parsedData.extrudedDirection),
            sceneUnitsFromMm(parsedData.fontSize ?? mmFromSceneUnits(CircleAnnotationData.DEFAULT_FONT_SIZE)),
            parsedData.showAreaText ?? true,
            parsedData.showVolume ?? false,
            parsedData.uuid,
            parsedData.parentId ?? undefined
        );
    }

}

export default CircleAnnotationData;
